import React, { useMemo, useRef, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined';
import Notification from '@this/src/notification';
import { FEEDBACK_TYPES, type FeedbackType } from '../../types/messages';
import FeedbackCommentPopup, { type PopupAlign } from '../message_section/FeedbackCommentPopup';

type Props = {
  currentFeedback: FeedbackType | null;
  commentSubmitted: boolean;
  // 評価対象ごとに誘導文が変わるため、呼び出し側から渡す
  promptTexts: Record<FeedbackType, string>;
  onSubmit: (feedbackType: FeedbackType, comment?: string) => Promise<void>;
  popupAlign?: PopupAlign;
};

const FEEDBACK_ICONS: Record<
  FeedbackType,
  {
    buttonLabel: string;
    selectedLabel: string;
    filledIcon: typeof ThumbUpIcon;
    outlinedIcon: typeof ThumbUpOutlinedIcon;
  }
> = {
  good: {
    buttonLabel: 'good',
    selectedLabel: 'good-selected',
    filledIcon: ThumbUpIcon,
    outlinedIcon: ThumbUpOutlinedIcon
  },
  bad: {
    buttonLabel: 'bad',
    selectedLabel: 'bad-selected',
    filledIcon: ThumbDownIcon,
    outlinedIcon: ThumbDownOutlinedIcon
  }
};

// Good / Bad フィードバックを送信するためのコンポーネント。
// 評価の状態は3つあり、見た目とクリック時の挙動が変わる。
//   未評価: good / bad の両方を輪郭アイコンで出す。押すと送信し、成功後にコメント欄を開く
//   評価済み・コメント未送信: 押した側だけ塗りつぶす。もう一度押すとコメント欄を再表示する
//   評価済み・コメント送信済み: 押した側だけ塗りつぶす。押しても何も起きない
// 一度評価すると反対側のアイコンは消える（評価値の変更はできない）。
const FeedbackButtons = ({ currentFeedback, commentSubmitted, promptTexts, onSubmit, popupAlign }: Props) => {
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [popupTarget, setPopupTarget] = useState<FeedbackType | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleRatingClick = async (feedbackType: FeedbackType) => {
    if (currentFeedback === null) {
      if (isSubmittingRating) return;
      setIsSubmittingRating(true);
      try {
        await onSubmit(feedbackType);
        setPopupTarget(feedbackType);
      } catch {
        Notification.error('フィードバックの送信に失敗しました。時間をおいてもう一度お試しください。');
      } finally {
        setIsSubmittingRating(false);
      }
      return;
    }
    if (currentFeedback === feedbackType && !commentSubmitted) {
      setPopupTarget(feedbackType);
    }
  };

  const handleSubmitComment = async (comment: string) => {
    if (popupTarget === null || isSubmittingComment) return;
    setIsSubmittingComment(true);
    try {
      await onSubmit(popupTarget, comment);
      Notification.success('フィードバックありがとうございます');
      setPopupTarget(null);
    } catch {
      Notification.error('フィードバックの送信に失敗しました。時間をおいてもう一度お試しください。');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const styles = useMemo(() => getStyles(), []);

  return (
    <div ref={containerRef} style={styles.container}>
      {(Object.values(FEEDBACK_TYPES) as FeedbackType[]).map(feedbackType => {
        const { buttonLabel, selectedLabel, filledIcon, outlinedIcon } = FEEDBACK_ICONS[feedbackType];
        const isSelected = currentFeedback === feedbackType;

        if (isSelected) {
          const ariaLabel = selectedLabel;
          if (commentSubmitted) {
            return (
              <div key={feedbackType} aria-label={ariaLabel} style={styles.selectedIcon}>
                {React.createElement(filledIcon, { style: styles.icon })}
              </div>
            );
          }
          return (
            <IconButton
              key={feedbackType}
              aria-label={ariaLabel}
              size="small"
              onClick={() => handleRatingClick(feedbackType)}
            >
              {React.createElement(filledIcon, { style: styles.icon })}
            </IconButton>
          );
        }

        if (currentFeedback !== null) return null;

        return (
          <IconButton
            key={feedbackType}
            aria-label={buttonLabel}
            size="small"
            disabled={isSubmittingRating}
            onClick={() => handleRatingClick(feedbackType)}
          >
            {React.createElement(outlinedIcon, { style: styles.icon })}
          </IconButton>
        );
      })}
      {popupTarget !== null && (
        <FeedbackCommentPopup
          promptText={promptTexts[popupTarget]}
          anchorEl={containerRef.current}
          align={popupAlign}
          disabled={isSubmittingComment}
          onSubmit={handleSubmitComment}
          onClose={() => setPopupTarget(null)}
        />
      )}
    </div>
  );
};

const getStyles = () =>
  ({
    container: {
      display: 'flex',
      gap: '4px',
      marginTop: '4px'
    },
    selectedIcon: {
      color: '#111111',
      display: 'flex',
      alignItems: 'center',
      padding: '4px'
    },
    icon: {
      fontSize: '16px'
    }
  } as const);

export default FeedbackButtons;
